import React, { useCallback, useEffect, useState } from 'react';
import { Send, Hash, Clock, AlertCircle } from 'lucide-react';
import { adminService, AdminChannel, AdminScheduledMessage } from '../../services/adminService';
import { ScheduleMessageModal } from '../ScheduleMessageModal';
import { ConfirmDialog } from '../ConfirmDialog';
import { useToast } from '../../contexts/ToastContext';
import { ADMIN_LIST_CAPPED } from './adminLayout';

/**
 * 관리자 콘솔 '공지 발송' — 공개 채널에 관리자 시스템 메시지를 즉시 보내거나 예약한다.
 * 예약 발송은 서버 스윕이 즉시 발송과 동등하게 수행한다(scheduledMessages.js의 is_admin_notice 경로).
 * 다른 관리자 세션과의 실시간 동기화는 없다(화면 재진입 시 refetch — 의도된 한계).
 * 예전엔 AdminConsole 안의 '메시지 발송' 탭이었다(2026-11 분리·이름 변경).
 */
export const NoticeSender: React.FC = () => {
  const { showToast } = useToast();
  const [channels, setChannels] = useState<AdminChannel[]>([]);
  const [channelsLoaded, setChannelsLoaded] = useState(false);
  const [selectedChannelId, setSelectedChannelId] = useState<number | null>(null);
  const [messageContent, setMessageContent] = useState('');
  const [sending, setSending] = useState(false);
  const [sendSuccess, setSendSuccess] = useState<string | null>(null);

  const [scheduleMode, setScheduleMode] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [scheduledList, setScheduledList] = useState<AdminScheduledMessage[]>([]);
  const [editingScheduled, setEditingScheduled] = useState<AdminScheduledMessage | null>(null);
  const [cancelScheduledTarget, setCancelScheduledTarget] = useState<AdminScheduledMessage | null>(null);

  const loadScheduled = useCallback(async () => {
    try {
      setScheduledList(await adminService.listScheduledMessages());
    } catch (err) {
      console.error('Failed to load scheduled notices:', err);
    }
  }, []);

  useEffect(() => {
    adminService.getPublicChannels()
      .then(setChannels)
      .catch((err) => {
        console.error('Failed to load admin channels:', err);
        showToast({ type: 'error', message: '채널을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.' });
      })
      .finally(() => setChannelsLoaded(true));
    loadScheduled();
  }, [loadScheduled, showToast]);

  const toggleScheduleMode = (on: boolean) => {
    setScheduleMode(on);
    if (on && !scheduleDate) {
      // 기본값: 1시간 뒤 (분 내림) — ScheduleMessageModal과 동일
      const base = new Date(Date.now() + 60 * 60 * 1000);
      base.setSeconds(0, 0);
      const pad = (n: number) => String(n).padStart(2, '0');
      setScheduleDate(`${base.getFullYear()}-${pad(base.getMonth() + 1)}-${pad(base.getDate())}`);
      setScheduleTime(`${pad(base.getHours())}:${pad(base.getMinutes())}`);
    }
  };

  const formatScheduledAt = (iso: string) => {
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '' : d.toLocaleString('ko-KR', {
      month: 'long', day: 'numeric', weekday: 'short', hour: 'numeric', minute: '2-digit',
    });
  };

  const cancelScheduled = async (item: AdminScheduledMessage) => {
    try {
      await adminService.cancelScheduledMessage(item.id);
      setScheduledList(prev => prev.filter(r => String(r.id) !== String(item.id)));
      showToast({ type: 'success', message: item.failedAt ? '예약을 삭제했습니다.' : '예약을 취소했습니다.' });
    } catch (err: any) {
      showToast({ type: 'error', message: err.message || '예약을 취소하지 못했습니다. 잠시 후 다시 시도해 주세요.' });
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedChannelId || !messageContent.trim()) return;

    // 예약 발송 — 즉시 발송과 같은 폼에서 시각만 붙는다. 범위(1분 뒤~30일 안)는 서버가 최종 판정
    if (scheduleMode) {
      const [y, m, d] = scheduleDate.split('-').map(Number);
      const [hh, mm] = scheduleTime.split(':').map(Number);
      const when = y && m && d && !Number.isNaN(hh) && !Number.isNaN(mm)
        ? new Date(y, m - 1, d, hh, mm, 0, 0)
        : null;
      if (!when || isNaN(when.getTime())) {
        showToast({ type: 'error', message: '예약 시간 형식이 올바르지 않습니다.' });
        return;
      }
      setSending(true);
      setSendSuccess(null);
      try {
        await adminService.scheduleMessage(selectedChannelId, messageContent.trim(), when.toISOString());
        const channelName = channels.find(ch => ch.id === selectedChannelId)?.name;
        setSendSuccess(`'${channelName}' 채널에 공지를 예약했습니다.`);
        setMessageContent('');
        setTimeout(() => setSendSuccess(null), 3000);
        loadScheduled();
      } catch (err: any) {
        showToast({ type: 'error', message: err.message || '공지를 예약하지 못했습니다. 잠시 후 다시 시도해 주세요.' });
      } finally {
        setSending(false);
      }
      return;
    }

    setSending(true);
    setSendSuccess(null);
    try {
      await adminService.sendMessage(selectedChannelId, messageContent.trim());
      const channelName = channels.find(ch => ch.id === selectedChannelId)?.name;
      setSendSuccess(`'${channelName}' 채널에 메시지가 전송되었습니다.`);
      setMessageContent('');
      setTimeout(() => setSendSuccess(null), 3000);
    } catch (err: any) {
      showToast({ type: 'error', message: err.message || '메시지를 보내지 못했습니다.' });
    } finally {
      setSending(false);
    }
  };

  const activeChannels = channels.filter(ch => !ch.isArchived);
  const selectedChannel = channels.find(ch => ch.id === selectedChannelId);

  return (
    <div>
      {/* Channel selector */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-slate-700 mb-2">대상 채널 선택</label>
        <div className={`space-y-1.5 ${ADMIN_LIST_CAPPED}`}>
          {activeChannels.map(channel => (
            <button
              key={channel.id}
              onClick={() => setSelectedChannelId(channel.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors text-left ${selectedChannelId === channel.id
                  ? 'bg-brand-600 text-white'
                  : 'bg-slate-50 text-slate-700 hover:bg-slate-100 border border-slate-200'
                }`}
            >
              <Hash size={16} className="shrink-0 opacity-70" />
              <div className="min-w-0">
                <div className="truncate font-medium">{channel.name}</div>
                {channel.description && (
                  <div className={`text-xs truncate ${selectedChannelId === channel.id ? 'text-white/80' : 'text-slate-400'
                    }`}>
                    {channel.description}
                  </div>
                )}
              </div>
            </button>
          ))}
          {channelsLoaded && activeChannels.length === 0 && (
            <div className="text-center py-8">
              <p className="text-sm font-medium text-slate-600">발송할 수 있는 공개 채널이 없습니다</p>
              <p className="text-xs text-slate-400 mt-1">'공개 채널' 메뉴에서 공개 채널을 먼저 만들어 주세요</p>
            </div>
          )}
        </div>
      </div>

      {/* Message input */}
      {selectedChannel && (
        <form onSubmit={handleSendMessage} className="mt-4">
          <div className="mb-2 flex items-center gap-2">
            <Hash size={14} className="text-brand-600" />
            <span className="text-sm font-medium text-brand-600">{selectedChannel.name}</span>
            <span className="text-xs text-slate-400">에 메시지 발송</span>
          </div>
          <div className="relative">
            <textarea aria-label="발송할 메시지"
              value={messageContent}
              onChange={(e) => setMessageContent(e.target.value)}
              placeholder="메시지를 입력하세요..."
              rows={4}
              className="w-full px-4 py-3 text-base md:text-sm border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent resize-none"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                  handleSendMessage(e);
                }
              }}
            />
          </div>
          {/* 예약 발송 옵션 — 체크 시 버튼이 '전송' 대신 '예약'이 된다 */}
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={scheduleMode}
                onChange={(e) => toggleScheduleMode(e.target.checked)}
                className="w-4 h-4 rounded border-slate-300 text-brand-600 focus:ring-brand-400"
              />
              예약 발송
            </label>
            {scheduleMode && (
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  aria-label="예약 날짜"
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)}
                  className="px-2.5 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                />
                <input
                  type="time"
                  aria-label="예약 시각"
                  step={60}
                  value={scheduleTime}
                  onChange={(e) => setScheduleTime(e.target.value)}
                  className="px-2.5 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                />
              </div>
            )}
          </div>
          {scheduleMode && (
            <p className="mt-1.5 text-xs text-slate-400">1분 뒤부터 30일 안까지 선택할 수 있습니다</p>
          )}
          <div className="flex items-center justify-between mt-3">
            <span className="text-xs text-slate-400">Ctrl + Enter로 전송</span>
            <button
              type="submit"
              disabled={sending || !messageContent.trim()}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50"
            >
              {scheduleMode ? <Clock size={14} /> : <Send size={14} />}
              {scheduleMode
                ? (sending ? '예약 중...' : '예약')
                : (sending ? '전송 중...' : '전송')}
            </button>
          </div>
          {sendSuccess && (
            <div className="mt-3 px-3 py-2 bg-green-50 text-green-700 text-sm rounded-lg border border-green-200">
              {sendSuccess}
            </div>
          )}
        </form>
      )}

      {/* 예약된 공지 목록 — 예약자가 아니어도 권한 있는 관리자면 취소·수정 가능(담당자 부재 대응) */}
      {scheduledList.length > 0 && (
        <div className="mt-6 pt-4 border-t border-slate-200">
          <h4 className="text-sm font-semibold text-slate-700 mb-2">예약된 공지</h4>
          <ul className="space-y-2">
            {scheduledList.map(item => (
              <li key={item.id} className="px-3 py-2.5 rounded-lg bg-slate-50 border border-slate-200">
                <div className="flex items-center gap-1.5 text-xs font-semibold">
                  {item.failedAt ? (
                    <AlertCircle size={13} className="text-brand-700 shrink-0" aria-hidden="true" />
                  ) : (
                    <Clock size={13} className="text-slate-400 shrink-0" aria-hidden="true" />
                  )}
                  <span className={item.failedAt ? 'text-brand-700' : 'text-slate-600'}>
                    {formatScheduledAt(item.scheduledAt)}
                  </span>
                  <span className="text-slate-400 font-normal truncate">
                    · #{item.channelName || item.channelId} · {item.senderName}
                  </span>
                </div>
                <p className="mt-1 text-sm text-slate-700 whitespace-pre-wrap break-words line-clamp-2">
                  {item.content}
                </p>
                {item.failedAt && item.failReason && (
                  <p className="mt-1 text-xs text-brand-700">{item.failReason}</p>
                )}
                <div className="mt-2 flex gap-2">
                  {!item.failedAt && (
                    <button
                      type="button"
                      onClick={() => setEditingScheduled(item)}
                      className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
                    >
                      수정
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => (item.failedAt ? cancelScheduled(item) : setCancelScheduledTarget(item))}
                    className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-white border border-brand-600 text-brand-600 hover:bg-brand-50 transition-colors"
                  >
                    {item.failedAt ? '삭제' : '취소'}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* 공지 예약 수정 — 채팅의 예약 수정과 같은 모달을 재사용한다 */}
      {editingScheduled && (
        <ScheduleMessageModal
          isOpen
          mode="edit"
          initialText={editingScheduled.content}
          initialScheduledAt={editingScheduled.scheduledAt}
          onConfirm={async (text, iso) => {
            try {
              const updated = await adminService.updateScheduledMessage(editingScheduled.id, text, iso);
              setScheduledList(prev => prev.map(r => (String(r.id) === String(updated.id) ? updated : r)));
              setEditingScheduled(null);
              showToast({ type: 'success', message: '저장했습니다.' });
              return true;
            } catch (err: any) {
              showToast({ type: 'error', message: err.message || '예약을 저장하지 못했습니다. 잠시 후 다시 시도해 주세요.' });
              return false;
            }
          }}
          onClose={() => setEditingScheduled(null)}
        />
      )}

      {/* 공지 예약 취소 확인 (실패 행 삭제는 확인 없이 즉시) */}
      {cancelScheduledTarget && (
        <ConfirmDialog
          isOpen
          title="예약 취소"
          message="이 공지의 예약 발송을 취소할까요? 메시지는 전송되지 않습니다."
          confirmLabel="예약 취소"
          danger
          onConfirm={() => {
            cancelScheduled(cancelScheduledTarget);
            setCancelScheduledTarget(null);
          }}
          onCancel={() => setCancelScheduledTarget(null)}
        />
      )}
    </div>
  );
};
